/**
 * Care circle: siblings, caregivers, permissions (view / edit / mark tasks done / assign tasks).
 */
import { Platform } from 'react-native';
import type { CareCircle, CareCircleMember, CareMemberPermissions } from '../types/care';

const COLLECTION = 'care';
const CIRCLE_DOC = 'circle';

function getDb(): Promise<any> {
  if (Platform.OS === 'web') {
    return import('./unifiedAuthService').then((m) => m.getWebDb());
  }
  const { initializeFirebase, getMobileFirestore } = require('./unifiedAuthService');
  if (!initializeFirebase()) return Promise.resolve(null);
  const getFs = getMobileFirestore();
  return Promise.resolve(getFs ? getFs() : null);
}

function circlePath(recipientId: string): string {
  return `users/${recipientId}/${COLLECTION}/${CIRCLE_DOC}`;
}

const DEFAULT_PERMISSIONS: Record<CareCircleMember['role'], CareMemberPermissions> = {
  coordinator: { view: true, edit: true, markTasksDone: true, assignTasks: true },
  family: { view: true, edit: false, markTasksDone: true, assignTasks: false },
  caregiver: { view: true, edit: false, markTasksDone: true, assignTasks: false },
};

export const careCircleService = {
  async get(recipientId: string): Promise<CareCircle | null> {
    const db = await getDb();
    if (!db) return null;
    try {
      if (Platform.OS === 'web') {
        const { doc, getDoc } = await import('firebase/firestore');
        const snap = await getDoc(doc(db, circlePath(recipientId)));
        if (!snap.exists()) return null;
        return snap.data() as CareCircle;
      }
      const ref = db.collection('users').doc(recipientId).collection(COLLECTION).doc(CIRCLE_DOC);
      const snap = await ref.get();
      if (!snap.exists) return null;
      return snap.data() as CareCircle;
    } catch (e) {
      console.warn('careCircleService.get error:', e);
      return null;
    }
  },

  async getMembers(recipientId: string): Promise<CareCircleMember[]> {
    const circle = await this.get(recipientId);
    return circle?.members ?? [];
  },

  async addMember(
    recipientId: string,
    member: Omit<CareCircleMember, 'id' | 'status' | 'invitedAt' | 'joinedAt' | 'inviteToken'> & { status?: 'active' | 'pending'; phone?: string }
  ): Promise<CareCircleMember> {
    const circle = await this.get(recipientId);
    const members = circle?.members ?? [];
    const now = new Date().toISOString();
    const status = member.status ?? 'pending';
    const inviteToken = status === 'pending' ? `inv_${Date.now()}_${Math.random().toString(36).slice(2, 11)}` : undefined;
    const newMember: CareCircleMember = {
      ...member,
      id: `mem_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
      status,
      permissions: member.permissions ?? DEFAULT_PERMISSIONS[member.role],
      invitedAt: (status === 'pending' ? now : undefined) as string | undefined,
      joinedAt: status === 'active' ? now : undefined,
      inviteToken,
      phone: member.phone,
    };
    members.push(newMember);
    await this.save(recipientId, { recipientId, members, updatedAt: now });
    return newMember;
  },

  async updateMemberPermissions(
    recipientId: string,
    memberId: string,
    permissions: Partial<CareMemberPermissions>
  ): Promise<void> {
    const circle = await this.get(recipientId);
    if (!circle) return;
    const member = circle.members.find((m) => m.id === memberId);
    if (!member) return;
    member.permissions = { ...member.permissions, ...permissions };
    await this.save(recipientId, { ...circle, updatedAt: new Date().toISOString() });
  },

  async removeMember(recipientId: string, memberId: string): Promise<void> {
    const circle = await this.get(recipientId);
    if (!circle) return;
    circle.members = circle.members.filter((m) => m.id !== memberId);
    await this.save(recipientId, { ...circle, updatedAt: new Date().toISOString() });
  },

  /**
   * Accept care invite: verify token, update member (userId, status), update user (linkedCareRecipientId).
   * Returns { success, error? }.
   */
  async acceptInvite(
    recipientId: string,
    memberId: string,
    token: string,
    acceptingUserId: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const circle = await this.get(recipientId);
      if (!circle) return { success: false, error: 'Care circle not found' };
      const member = circle.members.find((m) => m.id === memberId);
      if (!member) return { success: false, error: 'Invite not found' };
      if (member.status === 'active') return { success: false, error: 'Invite already accepted' };
      if (member.inviteToken !== token) return { success: false, error: 'Invalid or expired invite' };

      const now = new Date().toISOString();
      member.userId = acceptingUserId;
      member.status = 'active';
      member.joinedAt = now;
      delete member.inviteToken;
      await this.save(recipientId, { ...circle, updatedAt: now });

      const careRole = member.role === 'caregiver' ? 'professional' : 'family';
      await this.updateUserLinkedRecipient(acceptingUserId, recipientId, careRole);
      return { success: true };
    } catch (e: any) {
      console.warn('acceptInvite error:', e);
      return { success: false, error: e?.message ?? 'Failed to accept invite' };
    }
  },

  async updateUserLinkedRecipient(userId: string, recipientId: string, careRole?: 'family' | 'professional'): Promise<void> {
    const db = await getDb();
    if (!db) return;
    const updates: Record<string, unknown> = { linkedCareRecipientId: recipientId };
    if (careRole) updates.careRole = careRole;
    try {
      if (Platform.OS === 'web') {
        const { doc, updateDoc } = await import('firebase/firestore');
        await updateDoc(doc(db, 'users', userId), updates);
      } else {
        const { doc, updateDoc } = require('@react-native-firebase/firestore');
        const getFs = require('./unifiedAuthService').getMobileFirestore;
        const fs = getFs?.();
        if (fs) await updateDoc(doc(fs, 'users', userId), updates);
      }
    } catch (e) {
      console.warn('updateUserLinkedRecipient error:', e);
    }
  },

  async save(recipientId: string, circle: CareCircle): Promise<void> {
    const db = await getDb();
    if (!db) return;
    if (Platform.OS === 'web') {
      const { doc, setDoc } = await import('firebase/firestore');
      await setDoc(doc(db, circlePath(recipientId)), circle, { merge: true });
    } else {
      await db.collection('users').doc(recipientId).collection(COLLECTION).doc(CIRCLE_DOC).set(circle, { merge: true });
    }
  },

  /** Check if current user can perform action (caller passes member or permissions). */
  canView(member: CareCircleMember | null): boolean {
    return member?.permissions?.view ?? false;
  },
  canEdit(member: CareCircleMember | null): boolean {
    return member?.permissions?.edit ?? false;
  },
  canMarkTasksDone(member: CareCircleMember | null, taskAssignedToUserId?: string, currentUserId?: string): boolean {
    if (!member) return false;
    if (taskAssignedToUserId && currentUserId && taskAssignedToUserId === currentUserId) return true;
    return member.permissions?.markTasksDone ?? false;
  },
  canAssignTasks(member: CareCircleMember | null): boolean {
    return member?.permissions?.assignTasks ?? false;
  },

  DEFAULT_PERMISSIONS,
};
